import { useEffect, useMemo } from "react";
import { setCoverageState } from "reducers/future-medical";
import {
  useGetPartABPlanCoveragesQuery,
  useGetPartCDPlanCoveragesQuery,
} from "services/part-coverage-api";
import { useDispatch } from "utils/redux";

import { useCoverage } from "./useCoverage";

export const useCoverageData = (
  fileId: number | undefined,
  isCoverageComplete?: boolean,
  isNoCoverage?: boolean,
) => {
  const dispatch = useDispatch();

  const { data: partABCoverages, isLoading: isLoadingAB } =
    useGetPartABPlanCoveragesQuery({ file_id: fileId || 0 }, { skip: !fileId });

  const { data: partCDCoverages, isLoading: isLoadingCD } =
    useGetPartCDPlanCoveragesQuery({ file_id: fileId || 0 }, { skip: !fileId });

  const isLoading = isLoadingAB || isLoadingCD;

  const planCoverages = useMemo(
    () => ({
      part_a_b_coverages: partABCoverages?.results ?? [],
      part_c_d_coverages: partCDCoverages?.results ?? [],
    }),
    [partABCoverages, partCDCoverages],
  );

  const { hasCoverage, hasCoverageValidationErrors } =
    useCoverage(planCoverages);

  // combine and sort all coverages by created date (most recent first)
  const allCoverages = useMemo(() => {
    return [
      ...planCoverages.part_a_b_coverages.map((coverage) => ({
        ...coverage,
        coverageType: "AB" as const,
      })),
      ...planCoverages.part_c_d_coverages.map((coverage) => ({
        ...coverage,
        coverageType: "CD" as const,
      })),
    ].sort((a, b) => {
      const dateA = new Date(a.created).getTime();
      const dateB = new Date(b.created).getTime();
      return dateB - dateA;
    });
  }, [planCoverages]);

  // sync state to redux for right sidebar
  useEffect(() => {
    dispatch(
      setCoverageState({
        hasCoverage,
        coverageComplete: isCoverageComplete || false,
        noCoverage: isNoCoverage || false,
      }),
    );
  }, [dispatch, hasCoverage, isCoverageComplete, isNoCoverage]);

  return {
    allCoverages,
    isLoading,
    hasCoverage,
    hasCoverageValidationErrors,
  };
};
